import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import dayjs from "dayjs";
import { Formik } from "formik";
import { styles } from "./styles";
import Loader from "components/loader";
import React, { useState } from "react";
import ROUTE_NAMES from "routes/routeNames";
import { useLocation, useNavigate } from "react-router-dom";
import { createCouponSchema } from "utils/schema";
import AnimateButton from "components/animateButton";
import { useDispatch, useSelector } from "react-redux";
import ImageDropzone from "components/input/ImageDropzone";
import {
  ALL_COUPON_CATEGORIES,
  COUPON_CATEGORY_OPTIONS,
  COUPON_DISCOUNT_UNIT_OPTIONS,
  FOLDER,
  INITIAL_COUPON_VALUES,
} from "utils/constants";
import { createCoupon, updateCoupon } from "../action";
import { mapCouponFormValues } from "utils/apiMappers";
import SHOW_TOAST from "utils/showToast";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { extractS3Key, UPLOAD_FILE_TO_S3 } from "utils/services";
import { isLocalPreviewUrl, isStoredMediaUrl } from "utils/commonFunctions";
import { ReducersModal } from "utils/modals";
import { validateFile } from "modules/userManagement/action";

/** Same image rules as the Company Logo flow: JPG/PNG, under 1 MB, max 900x900 px. */
const COUPON_IMAGE_ACCEPT = ".jpg,.jpeg,.png";
const COUPON_IMAGE_MAX_DIMENSION = 900;

/** Sentinel value for the "All Categories" option in the multi-select. */
const SELECT_ALL_CATEGORIES = "all";

const areAllCategoriesSelected = (selected: number[] = []) =>
  ALL_COUPON_CATEGORIES.every((category) => selected.includes(category));

const blockNonNumericKeys = (e: React.KeyboardEvent) => {
  if (e.code === "KeyE" || e.code === "Minus" || e.code === "Space")
    e.preventDefault();
};

const CreateCoupon = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const couponId = new URLSearchParams(window.location.search).get("couponId");
  const isEditMode = Boolean(couponId);
  const locationCoupon = location.state?.couponDetails;
  const listCoupon = useSelector((state: ReducersModal) =>
    (state.CouponReducer.result || []).find(
      (item) => item._id === couponId || item.id === couponId
    )
  );

  const [loading, setLoading] = useState(false);
  const [couponLoaded, setCouponLoaded] = useState(
    !isEditMode || Boolean(locationCoupon)
  );
  const [formInitialValues, setFormInitialValues] = useState(
    locationCoupon ? mapCouponFormValues(locationCoupon) : INITIAL_COUPON_VALUES
  );
  const fileRef = React.useRef<HTMLInputElement>(null);
  const imageFileRef = React.useRef<File | null>(null);
  const originalImageRef = React.useRef(locationCoupon?.image || "");

  React.useEffect(() => {
    if (!isEditMode || !couponId) return;

    const coupon =
      locationCoupon &&
      (locationCoupon._id === couponId || locationCoupon.id === couponId)
        ? locationCoupon
        : listCoupon;

    if (!coupon) {
      SHOW_TOAST(
        "Coupon details not found. Please select a coupon from the list.",
        "error"
      );
      navigate(ROUTE_NAMES.MAIN_ROUTES.COUPON);
      return;
    }

    originalImageRef.current = coupon.image || "";
    setFormInitialValues(mapCouponFormValues(coupon));
    setCouponLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponId, isEditMode]);

  const handleOnSubmit = async (values: any) => {
    setLoading(true);
    try {
      let image = values.image;
      if (imageFileRef.current) {
        image = await UPLOAD_FILE_TO_S3(imageFileRef.current, FOLDER.SERVICE);
      }

      const payload = { ...values, image };

      const onDone = ({ isSuccess }: { isSuccess: boolean }) => {
        setLoading(false);
        if (isSuccess) {
          navigate(ROUTE_NAMES.MAIN_ROUTES.COUPON);
        }
      };

      if (isEditMode && couponId) {
        const previous = originalImageRef.current;
        const replacedS3Urls =
          previous &&
          isStoredMediaUrl(previous) &&
          extractS3Key(previous) !== extractS3Key(image || "")
            ? [previous]
            : [];
        dispatch(
          updateCoupon(
            couponId,
            payload,
            onDone,
            replacedS3Urls,
            formInitialValues
          )
        );
        return;
      }
      dispatch(createCoupon(payload, onDone));
    } catch (error) {
      SHOW_TOAST(error, "error");
      setLoading(false);
    }
  };

  const onImagePicked = (
    evt: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: Function,
    currentPreview?: string
  ) => {
    const file = evt?.target?.files?.[0];
    evt.target.value = "";
    if (!file) return;

    validateFile(file, COUPON_IMAGE_MAX_DIMENSION)
      .then(() => {
        if (currentPreview && isLocalPreviewUrl(currentPreview)) {
          URL.revokeObjectURL(currentPreview);
        }
        imageFileRef.current = file;
        setFieldValue("image", URL.createObjectURL(file));
      })
      .catch((error) => SHOW_TOAST(error, "error"));
  };

  if (isEditMode && !couponLoaded) {
    return loading ? <Loader /> : null;
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={handleOnSubmit}
      initialValues={formInitialValues}
      validationSchema={createCouponSchema}
      validateOnMount
    >
      {({
        errors,
        handleBlur,
        handleChange,
        handleSubmit,
        touched,
        values,
        setFieldValue,
        setFieldTouched,
        dirty,
        isValid,
        isSubmitting,
      }) => {
        const allCategoriesSelected = areAllCategoriesSelected(
          values.categories
        );
        const valueLabel = values.isPercentage
          ? "Percentage Off"
          : "Coupon Value";

        return (
          <form noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ mt: "20" }} alignItems="stretch">
              {/* Column 1: Banner */}
              <Grid item xs={12} md={4} mt={1} sx={styles.bannerColumn}>
                <FormControl fullWidth>
                  <Box sx={styles.bannerDropzone}>
                    <ImageDropzone
                      variant="service"
                      title="Upload image"
                      hint="JPG or PNG, up to 1 MB"
                      imageUrl={values.image}
                      onClick={() => fileRef.current?.click()}
                    />
                  </Box>
                  <input
                    type="file"
                    ref={fileRef}
                    accept={COUPON_IMAGE_ACCEPT}
                    style={{ display: "none" }}
                    onChange={(event) =>
                      onImagePicked(event, setFieldValue, values.image)
                    }
                  />
                  {touched.image && errors.image && (
                    <FormHelperText error id="helper-image">
                      {errors.image.toString()}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Column 2: Fields */}
              <Grid item xs={12} md={8} mt={1} sx={styles.fieldsColumn}>
                <Box sx={styles.fieldsGrid}>
                  {/* Row: identity */}
                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel htmlFor="couponCode-field">
                      {"Coupon Code"}
                    </InputLabel>
                    <OutlinedInput
                      type="text"
                      name="couponCode"
                      label="Coupon Code"
                      id="couponCode-field"
                      value={values.couponCode}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {touched.couponCode && errors.couponCode && (
                      <FormHelperText error id="helper-couponCode">
                        {errors.couponCode.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel htmlFor="title-field">{"Title"}</InputLabel>
                    <OutlinedInput
                      type="text"
                      name="title"
                      label="Title"
                      id="title-field"
                      value={values.title}
                      onBlur={handleBlur}
                      onChange={handleChange}
                    />
                    {touched.title && errors.title && (
                      <FormHelperText error id="helper-title">
                        {errors.title.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Row: discount */}
                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel htmlFor="couponValue-field">
                      {valueLabel}
                    </InputLabel>
                    <OutlinedInput
                      sx={{ ...styles.removeArrow, ...styles.attachedInput }}
                      type="number"
                      name="couponValue"
                      label={valueLabel}
                      id="couponValue-field"
                      value={values.couponValue}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyDown={blockNonNumericKeys}
                      endAdornment={
                        <InputAdornment
                          position="end"
                          sx={styles.attachedAdornment}
                        >
                          <Select
                            variant="standard"
                            disableUnderline
                            name="isPercentage"
                            value={values.isPercentage ? "true" : "false"}
                            sx={styles.attachedSelect}
                            inputProps={{ "aria-label": "Discount type" }}
                            renderValue={(selected) =>
                              COUPON_DISCOUNT_UNIT_OPTIONS.find(
                                (option) => option.id === selected
                              )?.symbol
                            }
                            onChange={(event) =>
                              setFieldValue(
                                "isPercentage",
                                event.target.value === "true"
                              )
                            }
                          >
                            {COUPON_DISCOUNT_UNIT_OPTIONS.map((option) => (
                              <MenuItem key={option.id} value={option.id}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </InputAdornment>
                      }
                    />
                    {touched.couponValue && errors.couponValue && (
                      <FormHelperText error id="helper-couponValue">
                        {errors.couponValue.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel id="categories-label">
                      {"Categories"}
                    </InputLabel>
                    <Select
                      labelId="categories-label"
                      multiple
                      name="categories"
                      label="Categories"
                      sx={styles.selectDropdown}
                      value={values.categories}
                      onBlur={handleBlur}
                      onChange={(event) => {
                        const next = event.target.value as Array<
                          number | string
                        >;
                        if (next.includes(SELECT_ALL_CATEGORIES)) {
                          setFieldValue(
                            "categories",
                            allCategoriesSelected
                              ? []
                              : [...ALL_COUPON_CATEGORIES]
                          );
                        } else {
                          setFieldValue(
                            "categories",
                            next.map((category) => Number(category))
                          );
                        }
                        setFieldTouched("categories", true, false);
                      }}
                      renderValue={(selected: any) =>
                        allCategoriesSelected
                          ? "All Categories"
                          : COUPON_CATEGORY_OPTIONS.filter((option) =>
                              (selected || []).includes(option.id)
                            )
                              .map((option) => option.label)
                              .join(", ")
                      }
                    >
                      <MenuItem value={SELECT_ALL_CATEGORIES}>
                        <Checkbox
                          checked={allCategoriesSelected}
                          indeterminate={
                            !allCategoriesSelected &&
                            values.categories.length > 0
                          }
                        />
                        <ListItemText primary="All Categories" />
                      </MenuItem>
                      {COUPON_CATEGORY_OPTIONS.map((option) => (
                        <MenuItem key={option.id} value={option.id}>
                          <Checkbox
                            checked={values.categories.includes(option.id)}
                          />
                          <ListItemText primary={option.label} />
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.categories && errors.categories && (
                      <FormHelperText error id="helper-categories">
                        {errors.categories.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Row: validity */}
                  <FormControl fullWidth sx={styles.fieldCell}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        name="startTime"
                        label="Start Time"
                        format="DD/MM/YYYY, hh:mm A"
                        ampm
                        disabled={isEditMode}
                        readOnly={isEditMode}
                        value={
                          values.startTime ? dayjs(values.startTime) : null
                        }
                        minDateTime={
                          isEditMode && values.startTime
                            ? dayjs(
                                Math.min(
                                  dayjs(values.startTime).valueOf(),
                                  dayjs().valueOf()
                                )
                              )
                            : dayjs()
                        }
                        onChange={(newValue) => {
                          setFieldValue(
                            "startTime",
                            newValue ? newValue.toISOString() : ""
                          );
                          setFieldTouched("startTime", true, false);
                        }}
                        onClose={() => setFieldTouched("startTime", true)}
                      />
                    </LocalizationProvider>
                    {touched.startTime && errors.startTime && (
                      <FormHelperText error id="helper-startTime">
                        {errors.startTime.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth sx={styles.fieldCell}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DateTimePicker
                        name="endTime"
                        label="End Time"
                        format="DD/MM/YYYY, hh:mm A"
                        ampm
                        value={values.endTime ? dayjs(values.endTime) : null}
                        minDateTime={
                          values.startTime ? dayjs(values.startTime) : dayjs()
                        }
                        onChange={(newValue) => {
                          setFieldValue(
                            "endTime",
                            newValue ? newValue.toISOString() : ""
                          );
                          setFieldTouched("endTime", true, false);
                        }}
                        onClose={() => setFieldTouched("endTime", true)}
                      />
                    </LocalizationProvider>
                    {touched.endTime && errors.endTime && (
                      <FormHelperText error id="helper-endTime">
                        {errors.endTime.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  {/* Row: redemption limits */}
                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel htmlFor="couponLevelRedemption-field">
                      {"Coupon Level Redemption"}
                    </InputLabel>
                    <OutlinedInput
                      sx={styles.removeArrow}
                      type="number"
                      name="couponLevelRedemption"
                      label="Coupon Level Redemption"
                      id="couponLevelRedemption-field"
                      value={values.couponLevelRedemption}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyDown={blockNonNumericKeys}
                    />
                    {touched.couponLevelRedemption &&
                      errors.couponLevelRedemption && (
                        <FormHelperText error id="helper-couponLevelRedemption">
                          {errors.couponLevelRedemption.toString()}
                        </FormHelperText>
                      )}
                  </FormControl>

                  <FormControl fullWidth sx={styles.fieldCell}>
                    <InputLabel htmlFor="userLevelRedemption-field">
                      {"User Level Redemption"}
                    </InputLabel>
                    <OutlinedInput
                      sx={styles.removeArrow}
                      type="number"
                      name="userLevelRedemption"
                      label="User Level Redemption"
                      id="userLevelRedemption-field"
                      value={values.userLevelRedemption}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyDown={blockNonNumericKeys}
                    />
                    {touched.userLevelRedemption &&
                      errors.userLevelRedemption && (
                        <FormHelperText error id="helper-userLevelRedemption">
                          {errors.userLevelRedemption.toString()}
                        </FormHelperText>
                      )}
                  </FormControl>

                  {/* Row: description (full width) */}
                  <FormControl
                    fullWidth
                    sx={{ ...styles.fieldCell, ...styles.fullRow }}
                  >
                    <InputLabel htmlFor="description-field">
                      {"Description"}
                    </InputLabel>
                    <OutlinedInput
                      multiline
                      rows={4}
                      name="description"
                      label="Description"
                      id="description-field"
                      value={values.description}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      sx={styles.descriptionInput}
                    />
                    {touched.description && errors.description && (
                      <FormHelperText error id="helper-description">
                        {errors.description.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={styles.footerRow}>
                  <FormControl>
                    <FormControlLabel
                      label="Set as default coupon"
                      control={
                        <Checkbox
                          name="isDefault"
                          checked={Boolean(values.isDefault)}
                          onBlur={handleBlur}
                          onChange={(event) =>
                            setFieldValue("isDefault", event.target.checked)
                          }
                        />
                      }
                    />
                    {touched.isDefault && errors.isDefault && (
                      <FormHelperText error id="helper-isDefault">
                        {errors.isDefault.toString()}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <AnimateButton>
                    <Button
                      disableElevation
                      size="large"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      disabled={!dirty || !isValid || isSubmitting || loading}
                    >
                      {isEditMode ? "Update Coupon" : "Create Coupon"}
                    </Button>
                  </AnimateButton>
                </Box>
              </Grid>
            </Grid>
            {loading && <Loader />}
          </form>
        );
      }}
    </Formik>
  );
};

export default CreateCoupon;
